import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, Form, Button, Checkbox, Input, Divider, Modal, Tour } from 'antd';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Section from 'components/Section';
import RodoText from 'components/RodoText';
import { useLocalStorage } from 'usehooks-ts';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import ProductOfTheYearCategory from './Category/ProductOfTheYearCategory';
import SubmitError from './Notifications/SubmitError';
import SubmitSuccess from './Notifications/SubmitSuccess';
import { merriweather } from 'styles/GlobalStyle';
import type { TourProps } from 'antd';
import ConfirmSubmitText from '../ConfirmSubmitText';

const FormSection = styled.p`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.72;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const RodoTextSpan = styled.span`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.greyishBrown};
`;

const InfoText = styled.span`
    font-size: 14px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.86;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
`;

const ButtonStyled = styled(Button)`
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
`;

export type Votes = {
    [categoryId: number]: string[];
};

type VotesState = Votes;

export interface FormValues {
    email: string;
    terms: boolean[];
}

type Props = {
    contest: POYContest;
};

export const ProductOfTheYearForm = ({ contest }: Props) => {
    const initialState = contest.categories
        .map((category) => category.id)
        .reduce((a, v) => ({ ...a, [v]: [] }), {});

    const [votes, setVotes] = useLocalStorage<VotesState>(
        `contest_${contest.seoSlug}`,
        initialState
    );
    const [loading, setLoading] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);
    const [tourClosed, setTourClosed] = useState(false);

    const [form] = Form.useForm();
    const router = useRouter();

    const categoriesRef = useRef(null);
    const formRef = useRef(null);
    const sendButtonRef = useRef(null);

    const steps: TourProps['steps'] = [
        {
            title: 'Jak głosować',
            description: 'Chcesz zobaczyć w jaki sposób głosować?',
            target: null,
            closable: true
        },
        {
            title: 'Jak głosować',
            description:
                'Aby wziąć udział w głosowaniu, kliknij przycisk "Zagłosuj" przy wybranych produktach',
            target: null
        },
        {
            title: 'Limit głosów',
            description:
                'W każdej z kategorii możesz oddać maksymalnie 3 głosy. Liczba głosów, która została jeszcze do oddania na produkty z danej kategorii wyświetla się obok nazwy tej kategorii.',
            target: () => categoriesRef.current?.firstChild
        },
        {
            title: 'Ostatni krok',
            description: 'Wpisz swój adres e-mail i zaznacz zgody na dole strony',
            target: () => formRef.current
        },
        {
            title: 'Wyślij swój głos',
            description: 'Kliknij przycisk "Wyślij", aby wysłać swój głos.',
            target: () => sendButtonRef.current
        },
        {
            title: 'Uwaga',
            description: <ConfirmSubmitText />,
            target: null
        }
    ];
    useEffect(() => {
        if (router.query.vote === 'true') {
            const categoryId = parseInt(router.query.voteCategory as string) as number;
            const articleSlug = router.query.voteArticle as string;

            if (votes[categoryId] !== undefined && !votes[categoryId].includes(articleSlug)) {
                addVote(categoryId, articleSlug);
            }
        }
    }, []);

    useEffect(() => {
        const onScroll = (e): void => {
            if (
                !tourClosed &&
                e.target.documentElement.scrollTop >= categoriesRef.current.offsetTop
            ) {
                setTourOpen(true);
            }
        };

        window.addEventListener('scroll', onScroll);

        return () => window.removeEventListener('scroll', onScroll);
    }, [categoriesRef, tourClosed]);

    const handleSubmit = (formValues) => {
        if (!Object.values(votes).some((cat) => cat.length > 0)) {
            toast.warning(
                <>
                    <h3>Nie oddałeś żadnego głosu</h3>
                    <p>Kliknij zagłosuj, aby oddać głos!</p>
                </>
            );
            return;
        }
        const { apiUrl } = getConfig().publicRuntimeConfig;
        setLoading(true);

        const { seoSlug } = contest;
        const { ...values } = formValues;
        const data = {
            email: values.email,
            agreement1: values.terms[0],
            agreement2: values.terms[1],
            contestData: { votes }
        };
        fetch(`${apiUrl}/public/api/contests/${seoSlug}/votes`, {
            method: 'POST',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
            .then((response) => {
                setLoading(false);
                if (!response.ok) throw response;

                return response.json();
            })
            .then(() => {
                form.resetFields();
                setVotes(initialState);
                Modal.success({ title: 'Oddałeś głos w konkursie', content: <SubmitSuccess /> });
            })
            .catch(async (response) => {
                const data = await response.json();
                if (data?.error === 'already_submitted') {
                    toast.error(<SubmitError type="already-submitted" />);
                    return;
                }
                if (data?.error === 'expired') {
                    toast.error(<SubmitError type="expired" />);
                    return;
                }
                toast.error(<SubmitError type="default" />);
            });
    };
    const handleVote = (categoryId: number, articleSlug: string) => {
        if (votes[categoryId] !== undefined && votes[categoryId].includes(articleSlug)) {
            removeVote(categoryId, articleSlug);
        } else {
            addVote(categoryId, articleSlug);
        }
    };

    const addVote = (categoryId: number, articleSlug: string) => {
        const category = contest.categories.find((category) => category.id === categoryId);
        if (category.maxVotes > 0 && votes[categoryId]?.length >= category.maxVotes) {
            toast.warning('Oddano maksymalną liczbę głosów w danej kategorii!');
            return;
        }

        setVotes((prev) => {
            if (prev[categoryId] === undefined) {
                return { ...prev, [categoryId]: [articleSlug] };
            }
            const categoryVotes = [...new Set([...prev[categoryId], articleSlug])];
            const contestVotes = {
                ...prev,
                [categoryId]: categoryVotes
            };
            return contestVotes;
        });
    };

    const removeVote = (categoryId: number, articleSlug: string) => {
        setVotes((prev) => {
            const categoryVotes = prev[categoryId];
            categoryVotes.splice(categoryVotes.indexOf(articleSlug), 1);
            return { ...prev, [categoryId]: categoryVotes };
        });
    };

    const showRodoModal = () => {
        Modal.info({
            closable: true,
            okText: 'Zamknij',
            width: '100%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <RodoText />,
            bodyStyle: { marginLeft: 0 }
        });
    };

    return (
        <>
            <Tour
                open={tourOpen}
                onClose={() => {
                    setTourOpen(false);
                    setTourClosed(true);
                    categoriesRef.current.scrollIntoView({ behavior: 'smooth' });
                }}
                steps={steps}
                disabledInteraction
                onFinish={() => {
                    categoriesRef.current.scrollIntoView({ behavior: 'smooth' });
                    setTourClosed(true);
                }}
            />
            <div ref={categoriesRef}>
                {contest.categories.map((category, index) => (
                    <>
                        <ProductOfTheYearCategory
                            key={`category__${category.id}`}
                            category={category}
                            handleVote={handleVote}
                            votes={votes[category.id]}
                            ref={(elem) => {
                                if (index === 0) categoriesRef.current = elem;
                            }}
                        />
                        <Divider />
                    </>
                ))}
            </div>

            <Col span={24} ref={formRef}>
                <Form
                    form={form}
                    name="vote"
                    onFinish={handleSubmit}
                    validateMessages={{
                        required: 'To pole jest wymagane!'
                    }}
                    scrollToFirstError
                >
                    <Section justify="center">
                        <Col xs={24} md={24}>
                            <FormSection>Aby zagłosować, pozostaw nam swoje dane</FormSection>
                        </Col>
                        <Col xs={24} md={{ span: 14 }} style={{ marginBottom: 25 }}>
                            <ConfirmSubmitText />
                        </Col>
                        <Col xs={24} md={14}>
                            <Form.Item
                                name="email"
                                rules={[
                                    {
                                        type: 'email',
                                        message: 'Podaj poprawny adres email'
                                    },
                                    {
                                        required: true
                                    }
                                ]}
                            >
                                <Input placeholder="E-mail*" />
                            </Form.Item>
                            <Form.Item
                                name={['terms', 0]}
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value
                                                ? Promise.resolve()
                                                : Promise.reject(
                                                      new Error('To pole jest wymagane!')
                                                  )
                                    }
                                ]}
                            >
                                <Checkbox style={{ lineHeight: '22px' }}>
                                    <small>
                                        Wyrażam zgodę na włączenie do zbioru i przetwarzanie moich
                                        danych osobowych w celach promocji i marketingu działalności
                                        prowadzonej przez Raven Media Sp. z o.o. z siedzibą we
                                        Wrocławiu (53-611), ul. Strzegomska 42AB (dalej Raven
                                        Media), świadczonych usług oraz oferowanych produktów.
                                        Wyrażam również zgodę na przetwarzanie danych osobowych w
                                        celu promocji ofert klientów Raven Media.
                                    </small>
                                </Checkbox>
                            </Form.Item>
                            <Form.Item
                                name={['terms', 1]}
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value
                                                ? Promise.resolve()
                                                : Promise.reject(
                                                      new Error('To pole jest wymagane!')
                                                  )
                                    }
                                ]}
                            >
                                <Checkbox style={{ lineHeight: '22px' }}>
                                    <small>
                                        Stosownie do Art. 10 Ustawy o świadczeniu usług drogą
                                        elektroniczną z dnia 18 lipca 2002 rejestrując się w
                                        serwisie wyrażają Państwo zgodę na otrzymywanie informacji
                                        handlowych od Raven Media Sp. z o.o. oraz jej klientów na
                                        adres elektroniczny podany w formularzu. Wyrażającemu zgodę
                                        na przetwarzanie danych osobowych przysługuje prawo do
                                        kontroli przetwarzania danych, które go dotyczą, a także
                                        prawo ich poprawiania.
                                    </small>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Section>

                    <Row>
                        <Col xs={24} md={{ push: 6, span: 12 }}>
                            <Row justify="end">
                                <Col>
                                    <InfoText>* Pola obowiązkowe</InfoText>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Section justify="center">
                        <Col>
                            <ButtonStyled htmlType="submit" disabled={loading} ref={sendButtonRef}>
                                Wyślij
                            </ButtonStyled>
                        </Col>
                    </Section>
                    <Section>
                        <Col xs={{ span: 24 }} md={{ offset: 6, span: 12 }}>
                            <RodoTextSpan>
                                RAVEN MEDIA sp. z o.o. informuje, że będzie administratorem
                                dotyczących Pani/Pana danych osobowych podanych w niniejszym
                                formularzu i w toku realizacji zamówionych w formularzu usług.
                                <Button onClick={showRodoModal} type="link">
                                    Więcej...
                                </Button>
                            </RodoTextSpan>
                        </Col>
                    </Section>
                </Form>
            </Col>
        </>
    );
};

export default ProductOfTheYearForm;
